// Transient action feedback — the state half of `<Toast/>` (spec §3.14).
//
// Every mutating surface used to hand-roll the same block: a signal, a timer, a
// `notify` that resets it, and an `onCleanup` to clear it. Fifteen lines to say
// "that worked", repeated per route — and predictably, about half the mutating
// actions in the console never got them (#924): verifying a key was silent both
// ways, adding a webhook said nothing, revoking a runner only spoke up when it
// FAILED. Feedback you have to re-implement per page is feedback that gets
// skipped, so it lives here once — and `src/testing/toast-seam.test.ts`
// refuses a hand-rolled `setTimeout(() => setX(null))` anywhere else.
//
// Deliberately NOT a global store: a route owns its own toast and renders its own
// `<Toast/>`, which keeps each surface unit-testable in isolation (a component
// rendered without the shell still shows its own notices) and keeps two pages
// from racing for one slot.

import { createSignal, onCleanup } from 'solid-js';

export type ToastTone = 'success' | 'info' | 'error' | 'warning';
export type ToastAction = { readonly label: string; readonly run: () => void };
export type ToastState = {
  readonly message: string;
  readonly tone: ToastTone;
  readonly action?: ToastAction;
};

/** How long a notice stays up before it fades itself out. */
const DEFAULT_TTL_MS = 3_500;

/**
 * The settings pages' shorter "Saved" / "Removed" flash (Notifications, Email
 * routes, org SSO) — confirmation of an edit the reader is looking at, not news.
 */
export const NOTICE_TTL_MS = 2_600;

export type ToastController = {
  /** Current notice, or null. Feed straight to `<Toast message=… tone=…/>`. */
  readonly toast: () => ToastState | null;
  /** Show a notice; tone defaults to `success` (the common case). */
  readonly notify: (message: string, tone?: ToastTone, action?: ToastAction) => void;
  /** Clear it now — wire to the `<Toast/>`'s `onDismiss`. */
  readonly dismiss: () => void;
};

/**
 * Toast state for one surface. Call in a component body: the auto-dismiss timer
 * is owned here and cleared on unmount, so a notice fired right before a route
 * change can't fire a `setState` into a disposed scope.
 *
 * A second `notify` replaces the first and restarts the clock, rather than
 * queueing — the newest outcome is the one worth reading.
 */
export function createToast(
  ttlMs: number = DEFAULT_TTL_MS,
  /**
   * Ran when a notice EXPIRES, just before it clears and while it is still on screen: where
   * a surface that moved focus onto its notice hands focus back (#6726). Not on `dismiss`,
   * not when a newer notice replaces it.
   */
  onExpire?: () => void,
): ToastController {
  const [toast, setToast] = createSignal<ToastState | null>(null);
  let timer: ReturnType<typeof setTimeout> | undefined;

  const dismiss = (): void => {
    clearTimeout(timer);
    setToast(null);
  };

  const notify = (message: string, tone: ToastTone = 'success', action?: ToastAction): void => {
    // `exactOptionalPropertyTypes`: an explicit `action: undefined` is not the
    // same as the key being absent, so it is included only when given.
    setToast(action === undefined ? { message, tone } : { message, tone, action });
    clearTimeout(timer);
    timer = setTimeout(() => {
      onExpire?.();
      setToast(null);
    }, ttlMs);
  };

  onCleanup(() => clearTimeout(timer));

  return { toast, notify, dismiss };
}
